import { useEffect, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Terminal, Package, Server, ArrowRight, ArrowLeft,
  Loader2, Info, Pencil, FileText,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSkill } from '@/hooks/useSkills';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { SKILL_KIND, MCP_KIND, createSkillTags, type SkillType } from '@/lib/skills';

interface SkillFormData {
  name: string;
  description: string;
  version: string;
  repository: string;
  homepage: string;
  license: string;
  price: string;
  installCommand: string;
  tags: string;
}

export default function EditSkill() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { data: skill, isLoading } = useSkill(identifier || '', user?.pubkey);
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const { toast } = useToast();

  useSeoMeta({
    title: skill ? `Edit ${skill.name} - SkillForge` : 'Edit Skill - SkillForge',
    description: 'Edit your published skill on SkillForge.',
  });

  const [type, setType] = useState<SkillType>('skill');
  const [formData, setFormData] = useState<SkillFormData>({
    name: '',
    description: '',
    version: '1.0.0',
    repository: '',
    homepage: '',
    license: '',
    price: '0',
    installCommand: '',
    tags: '',
  });

  // Pre-populate form once skill loads
  useEffect(() => {
    if (!skill) return;
    setType(skill.type);
    setFormData({
      name: skill.name,
      description: skill.description,
      version: skill.version,
      repository: skill.repository ?? '',
      homepage: skill.homepage ?? '',
      license: skill.license ?? '',
      price: String(skill.price ?? 0),
      installCommand: skill.installCommand ?? '',
      tags: skill.tags.join(', '),
    });
  }, [skill]);

  const handleChange = (field: keyof SkillFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !skill) return;

    if (!formData.name || !formData.description) {
      toast({ title: 'Missing fields', description: 'Name and description are required.', variant: 'destructive' });
      return;
    }

    const price = parseInt(formData.price, 10) || 0;
    const tags = formData.tags.split(',').map((t) => t.trim()).filter(Boolean);

    // createSkillTags keeps the same identifier (d tag) so this replaces the existing addressable event
    const skillTags = createSkillTags({
      identifier: skill.identifier,
      name: formData.name,
      description: formData.description,
      version: formData.version,
      type,
      repository: formData.repository || undefined,
      homepage: formData.homepage || undefined,
      license: formData.license || undefined,
      price,
      installCommand: formData.installCommand || undefined,
      tags,
    });

    try {
      await createEvent({
        kind: type === 'skill' ? SKILL_KIND : MCP_KIND,
        content: '',
        tags: skillTags,
      });

      toast({ title: 'Updated!', description: `"${formData.name}" has been updated on Nostr.` });
      navigate(`/skill/${user.pubkey}/${skill.identifier}`);
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  // Guard: not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <EditHeader />
        <main className="container mx-auto px-4 py-20 text-center">
          <p className="text-muted-foreground mb-4">You must be logged in to edit skills.</p>
          <LoginArea />
        </main>
      </div>
    );
  }

  // Guard: loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <EditHeader />
        <main className="container mx-auto px-4 py-10 max-w-2xl">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-4 w-72 mb-10" />
          <div className="space-y-6">
            <Skeleton className="h-48 rounded-2xl" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        </main>
      </div>
    );
  }

  // Guard: skill not found or not owned by current user
  if (!skill || skill.pubkey !== user.pubkey) {
    return (
      <div className="min-h-screen bg-background">
        <EditHeader />
        <main className="container mx-auto px-4 py-20 text-center">
          <Package className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Skill not found</h1>
          <p className="text-muted-foreground mb-6">
            This skill doesn't exist or you don't have permission to edit it.
          </p>
          <Link to="/my-skills">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              My Skills
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <EditHeader />

      <main className="container mx-auto px-4 py-10">
        <div className="max-w-2xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/my-skills" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft className="w-3 h-3" />
            My Skills
          </Link>

          {/* Page header */}
          <div className="flex items-center gap-3 mb-8">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border border-white/[0.08] ${
              skill.type === 'mcp'
                ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400'
                : 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 text-orange-400'
            }`}>
              {skill.type === 'mcp' ? <Server className="w-5 h-5" /> : <Package className="w-5 h-5" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{skill.name}</h1>
                <Pencil className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground font-mono">{skill.identifier}</p>
            </div>
          </div>

          {/* Type toggle — disabled since changing kind would create a new skill */}
          <div className="flex gap-3 mb-8">
            {(['skill', 'mcp'] as SkillType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                  type === t
                    ? t === 'mcp'
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                      : 'bg-orange-500/15 border-orange-500/40 text-orange-300'
                    : 'border-white/[0.08] text-muted-foreground hover:border-white/[0.15]'
                }`}
              >
                {t === 'mcp' ? <Server className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                {t === 'mcp' ? 'MCP Server' : 'OpenClaw Skill'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Basic Info</CardTitle>
                <CardDescription>Core details about your {type === 'skill' ? 'skill' : 'MCP server'}.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Identifier</Label>
                    <Input
                      value={skill.identifier}
                      disabled
                      className="font-mono h-11 bg-white/[0.02] border-white/[0.06] text-muted-foreground cursor-not-allowed"
                    />
                    <p className="text-xs text-muted-foreground">Cannot be changed — it's the event's d-tag</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={handleChange('description')}
                    rows={3}
                    className="bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                  />
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <FileText className="w-3 h-3 text-orange-400/70" />
                    Supports <strong className="text-orange-400/90 font-medium">Markdown</strong> — headings, bold, lists, links, and code blocks
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input
                      id="version"
                      value={formData.version}
                      onChange={handleChange('version')}
                      className="font-mono h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="license">License</Label>
                    <Input
                      id="license"
                      value={formData.license}
                      onChange={handleChange('license')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags</Label>
                    <Input
                      id="tags"
                      placeholder="web, automation"
                      value={formData.tags}
                      onChange={handleChange('tags')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Links */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Links</CardTitle>
                <CardDescription>Repository and documentation links.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="repository">Repository URL</Label>
                    <Input
                      id="repository"
                      value={formData.repository}
                      onChange={handleChange('repository')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="homepage">Homepage</Label>
                    <Input
                      id="homepage"
                      value={formData.homepage}
                      onChange={handleChange('homepage')}
                      className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className="glass-card border-dashed">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Pricing
                  <span className="text-xs font-normal text-muted-foreground bg-white/[0.05] px-2 py-0.5 rounded">Advanced</span>
                </CardTitle>
                <CardDescription>Most skills are free. Paid skills require a DVM server.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price (sats)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    value={formData.price}
                    onChange={handleChange('price')}
                    className="h-11 bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                  />
                </div>
                {parseInt(formData.price) > 0 && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-amber-400">DVM Server Required. </span>
                        Paid skills require a NIP-90 Data Vending Machine server.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Install */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Installation Instructions</CardTitle>
                <CardDescription>Optional. Leave blank to auto-generate.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Textarea
                  value={formData.installCommand}
                  onChange={handleChange('installCommand')}
                  rows={4}
                  className="font-mono text-sm bg-white/[0.02] border-white/[0.08] focus:border-orange-500/50"
                />
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <FileText className="w-3 h-3 text-orange-400/70" />
                  Supports <strong className="text-orange-400/90 font-medium">Markdown</strong> — paste a full README or write step-by-step instructions
                </p>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4">
              <Link to="/my-skills">
                <Button type="button" variant="ghost" className="h-11 px-6">Cancel</Button>
              </Link>
              <Button
                type="submit"
                disabled={isPending}
                className="gap-2 px-8 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-lg shadow-orange-500/25"
              >
                {isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Saving...</>
                ) : (
                  <>Save Changes<ArrowRight className="w-4 h-4" /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function EditHeader() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/[0.06]">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 flex items-center justify-center glow-primary-sm border border-white/[0.08] group-hover:border-white/[0.15] transition-all">
            <Terminal className="w-4 h-4 text-orange-400" />
          </div>
          <span className="font-semibold text-lg tracking-tight">SkillForge</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
          <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
          <Link to="/my-skills" className="hover:text-foreground transition-colors">My Skills</Link>
          <Link to="/connect-agent" className="hover:text-foreground transition-colors">Connect Agent</Link>
        </nav>
        <LoginArea className="max-w-60" />
      </div>
    </header>
  );
}
